/**
 * File Registry Service
 * 
 * Provides stable file references using UUIDs stored in Firestore.
 * Files are referenced by `file://{fileId}` in node data, which can be
 * resolved to current download URLs without breaking when files are renamed.
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getFirestore, doc, setDoc, getDoc, getDocs, deleteDoc, collection, query, where, onSnapshot, updateDoc, writeBatch } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL, deleteObject, listAll, getMetadata, uploadString } from 'firebase/storage';
import { firebaseConfig } from '../firebaseConfig';
import { mediaFileName } from './mediaNames';

// File reference prefix
export const FILE_REF_PREFIX = 'file://';

// Storage categories
export type StorageCategory =
    | 'Generated_Images'
    | 'Uploaded_Images'
    | 'Videos'
    | 'Audio'
    | 'Sketches'
    | 'Characters'
    | 'Sets'
    | 'Scripts'
    | 'Other';

// File metadata stored in Firestore at users/{userId}/files/{fileId}
export interface FileMetadata {
    id: string;              // UUID - never changes
    displayName: string;     // User-editable name
    storagePath: string;     // Current Firebase Storage path
    downloadUrl: string;     // Current download URL
    type: 'image' | 'video' | 'audio' | 'file';
    contentType: string;     // MIME type
    size: number;            // File size in bytes
    category: StorageCategory;
    createdAt: number;
    updatedAt: number;
    nodeId?: string;         // Optional: which node created this file
    nodeType?: string;       // Optional: e.g. 'ImageGenerator', 'VideoGenerator'
    workspaceName?: string;  // Optional: workspace this file belongs to
    contentHash?: string;    // SHA-256 hash for deduplication
    // Generation metadata
    prompt?: string;
    iterationCount?: number;
    negativePrompt?: string;
    model?: string;
}

// Get or initialize Firebase app
let firebaseApp: FirebaseApp | null = null;

const getFirebaseApp = (): FirebaseApp | null => {
    if (firebaseApp) return firebaseApp;
    
    const apps = getApps();
    if (apps.length > 0) {
        firebaseApp = apps[0];
        return firebaseApp;
    }
    
    if (firebaseConfig.apiKey && firebaseConfig.apiKey !== "YOUR_API_KEY_HERE") {
        firebaseApp = initializeApp(firebaseConfig);
        return firebaseApp;
    }
    
    return null;
};

// Check if Firebase is active
export const isFirebaseActive = (): boolean => {
    return getFirebaseApp() !== null;
};

// Generate a unique file ID
export const generateFileId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

// Check if a string is a file reference
export const isFileReference = (value: string | undefined | null): boolean => {
    return typeof value === 'string' && value.startsWith(FILE_REF_PREFIX);
};

// Extract file ID from file reference
export const extractFileId = (fileRef: string): string | null => {
    if (!isFileReference(fileRef)) return null;
    return fileRef.substring(FILE_REF_PREFIX.length);
};

// Create a file reference from file ID
export const createFileReference = (fileId: string): string => {
    return `${FILE_REF_PREFIX}${fileId}`;
};

/**
 * Calculate SHA-256 hash of base64 content for deduplication
 */
export const calculateContentHash = async (base64Data: string): Promise<string> => {
    // Extract just the base64 content (remove data:xxx;base64, prefix)
    const base64Content = base64Data.includes(',') 
        ? base64Data.split(',')[1] 
        : base64Data;
    
    // Check if crypto.subtle is available (requires HTTPS or localhost)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
        // Convert base64 to ArrayBuffer
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Calculate SHA-256 hash
        const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
    
    // Fallback: Simple hash for non-secure contexts (not cryptographically secure but good for deduplication)
    // Uses DJB2 algorithm on first 10KB + length for efficiency
    const sampleSize = Math.min(base64Content.length, 10000);
    let hash = 5381;
    for (let i = 0; i < sampleSize; i++) {
        hash = ((hash << 5) + hash) + base64Content.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
    }
    // Include full length to differentiate files with same prefix
    return `djb2_${Math.abs(hash).toString(16)}_${base64Content.length}`;
};

/**
 * Find an existing file by content hash (for deduplication)
 */
export const findByContentHash = async (
    userId: string,
    contentHash: string
): Promise<FileMetadata | null> => {
    const app = getFirebaseApp();
    if (!app) return null;
    
    const db = getFirestore(app);
    const filesRef = collection(db, 'users', userId, 'files');
    const q = query(filesRef, where('contentHash', '==', contentHash));
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    
    return snapshot.docs[0].data() as FileMetadata;
};

/**
 * Sanitize workspace name for use in storage paths
 */
const sanitizeWorkspaceName = (name: string): string => {
    return name
        .replace(/[^a-zA-Z0-9\s-_]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50) || 'Workspace';
};

/**
 * Get file size from base64 data URL
 */
const getBase64FileSize = (base64Data: string): number => {
    const base64Part = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
    const padding = (base64Part.match(/=/g) || []).length;
    return Math.floor((base64Part.length * 3) / 4) - padding;
};

/**
 * Upload a file with content-based deduplication
 * Returns a stable file:// reference
 * 
 * This is the main function nodes should use to upload files immediately.
 * It checks if the same content already exists (by hash) and reuses it if so.
 * @param options.timestamp Optional: use this timestamp for filename to match local save
 */
interface UploadWithDeduplicationOptions {
    category: StorageCategory;
    workspaceName: string;
    displayNamePrefix: string;
    extension: string;
    nodeId?: string;
    timestamp?: number;
    /** Called when the same bytes are already in the library, with that file, instead of uploading them. */
    onDuplicate?: (existing: FileMetadata) => void;
    // Generation metadata
    prompt?: string;
    iterationCount?: number;
    negativePrompt?: string;
    model?: string;
    nodeType?: string;
}

/**
 * Uploads in progress, keyed by their data URL. A workspace save that starts while a
 * node is still uploading the same bytes joins that upload instead of creating a
 * second file; the content-hash check cannot catch this because neither upload has
 * registered its file yet.
 */
const inFlightUploads = new Map<string, { userId: string; promise: Promise<string> }>();

/**
 * Blob URLs a node is uploading right now, and blob URLs already uploaded this
 * session mapped to their file references. The workspace save consults these so it
 * neither waits on a node's upload nor uploads the same bytes again, and so history
 * entries recorded against a blob URL are saved with the file reference instead.
 */
const blobUploadsInFlight = new Set<string>();
const uploadedBlobRefs = new Map<string, string>();

export const markBlobUploading = (blobUrl: string): void => {
    blobUploadsInFlight.add(blobUrl);
};

export const finishBlobUpload = (blobUrl: string, fileRef?: string): void => {
    blobUploadsInFlight.delete(blobUrl);
    if (fileRef && isFileReference(fileRef)) {
        uploadedBlobRefs.set(blobUrl, fileRef);
        rememberSessionBlob(fileRef, blobUrl);
    }
};

export const isBlobUploading = (blobUrl: string): boolean => blobUploadsInFlight.has(blobUrl);

/** The file reference a blob URL was uploaded as this session, if any. */
export const resolveUploadedBlob = (ref: string | undefined | null): string | undefined =>
    typeof ref === 'string' && ref.startsWith('blob:') ? uploadedBlobRefs.get(ref) : undefined;

/**
 * The other direction: for a file uploaded this session, the blob URL that still holds
 * its bytes in memory. Loaders read that instead of downloading what was just uploaded.
 * The URL may have been released since; readers must treat a failed read as a miss.
 */
const sessionBlobsByRef = new Map<string, string>();

export const rememberSessionBlob = (fileRef: string, blobUrl: string): void => {
    if (isFileReference(fileRef) && blobUrl.startsWith('blob:')) sessionBlobsByRef.set(fileRef, blobUrl);
};

export const sessionBlobForRef = (fileRef: string): string | undefined => sessionBlobsByRef.get(fileRef);

export const forgetSessionBlob = (fileRef: string): void => {
    sessionBlobsByRef.delete(fileRef);
};

/**
 * Goes up each time the session's memory is dropped (sign-out). A module that keeps things
 * for the session compares it and drops its own when it changed.
 */
let sessionNumber = 0;
export const sessionEpoch = (): number => sessionNumber;

/** Forget every in-memory copy, when the user signs out: the next account must not be served them. */
export const clearSessionBlobs = (): void => {
    sessionBlobsByRef.clear();
    forgetRegistrySession();
    sessionNumber++;
};

// ============================================
// WHAT THIS SESSION ALREADY KNOWS OF THE REGISTRY
// ============================================

/**
 * Registry entries read in this session, so the many places that show one file ask once.
 * Only entries that exist are kept: a file that was not found, or a read that failed, is
 * asked for again. Forgotten on sign-out.
 */
const metaLookups = new Map<string, Promise<FileMetadata | null>>();
const knownMeta = new Map<string, FileMetadata>();
const metaKey = (userId: string, fileId: string): string => `${userId}/${fileId}`;

/**
 * Where each of an account's files is stored in the cloud (file id to storage path),
 * remembered across sessions: the path names the file's local copy, so a file already seen
 * on this device is found in the local folder without asking the registry first. A path
 * never changes once its file is registered. Kept in memory and, a moment after it changes,
 * in localStorage under the account (the newest STORAGE_PATHS_LIMIT files).
 */
const STORAGE_PATHS_KEY = 'fileStoragePaths';
const STORAGE_PATHS_LIMIT = 2000;
const STORAGE_PATHS_WRITE_MS = 1000;
const storagePaths = new Map<string, Map<string, string>>();
const unsavedPathAccounts = new Set<string>();
let pathWriteTimer: ReturnType<typeof setTimeout> | null = null;

const pathsOf = (userId: string): Map<string, string> => {
    let paths = storagePaths.get(userId);
    if (!paths) {
        paths = new Map();
        try {
            const kept = JSON.parse(localStorage.getItem(`${userId}:${STORAGE_PATHS_KEY}`) || '{}');
            if (kept && typeof kept === 'object') {
                for (const [fileId, storagePath] of Object.entries(kept)) if (typeof storagePath === 'string') paths.set(fileId, storagePath);
            }
        } catch { /* unreadable: start empty */ }
        storagePaths.set(userId, paths);
    }
    return paths;
};

const writeStoragePaths = (): void => {
    if (pathWriteTimer) clearTimeout(pathWriteTimer);
    pathWriteTimer = null;
    for (const userId of unsavedPathAccounts) {
        const paths = storagePaths.get(userId);
        if (!paths) continue;
        try { localStorage.setItem(`${userId}:${STORAGE_PATHS_KEY}`, JSON.stringify(Object.fromEntries(paths))); } catch { /* storage full or switched off: memory still has them */ }
    }
    unsavedPathAccounts.clear();
};

const writeStoragePathsSoon = (userId: string): void => {
    unsavedPathAccounts.add(userId);
    if (!pathWriteTimer) pathWriteTimer = setTimeout(writeStoragePaths, STORAGE_PATHS_WRITE_MS);
};
// What was learnt in the last moment before the page goes is kept too
if (typeof window !== 'undefined') window.addEventListener('pagehide', writeStoragePaths);

/** Remember where a file is stored (see storagePaths). */
export const rememberStoragePath = (userId: string, fileId: string, storagePath: string | undefined | null): void => {
    if (!userId || !fileId || !storagePath) return;
    const paths = pathsOf(userId);
    if (paths.get(fileId) === storagePath) return;
    paths.set(fileId, storagePath);
    // The map keeps the order files were added in: the first are the oldest
    while (paths.size > STORAGE_PATHS_LIMIT) paths.delete(paths.keys().next().value as string);
    writeStoragePathsSoon(userId);
};

/** The storage path remembered for a file on this device, or undefined when it has not been seen. */
export const knownStoragePath = (userId: string | undefined | null, fileId: string): string | undefined =>
    (userId ? pathsOf(userId).get(fileId) : undefined);

const forgetFile = (userId: string, fileId: string): void => {
    metaLookups.delete(metaKey(userId, fileId));
    knownMeta.delete(metaKey(userId, fileId));
    if (pathsOf(userId).delete(fileId)) writeStoragePathsSoon(userId);
};

/** What a read of the registry said, kept for the session. */
const noteFileMeta = (userId: string, metadata: FileMetadata, fileId: string): void => {
    const key = metaKey(userId, fileId);
    knownMeta.set(key, metadata);
    metaLookups.set(key, Promise.resolve(metadata));
    rememberStoragePath(userId, fileId, metadata.storagePath);
};

/** What this session holds of the registry is dropped (sign-out). The paths kept on this device stay, under their account. */
const forgetRegistrySession = (): void => {
    writeStoragePaths();
    metaLookups.clear();
    knownMeta.clear();
    storagePaths.clear();
};

/**
 * A file's registry entry, read once per session however many places ask (lookups in
 * progress are shared). A missing entry and a failed read are not kept. Use getFileById to
 * read the registry anew.
 */
export const getFileMeta = (userId: string, fileId: string): Promise<FileMetadata | null> => {
    const key = metaKey(userId, fileId);
    const known = metaLookups.get(key);
    if (known) return known;
    const pending: Promise<FileMetadata | null> = getFileById(userId, fileId);
    metaLookups.set(key, pending);
    const forget = () => { if (metaLookups.get(key) === pending) metaLookups.delete(key); };
    pending.then((meta) => { if (!meta) forget(); }, forget);
    return pending;
};

/** The registry entry this session already read for a file, without asking; null when it has not. */
export const peekFileMeta = (userId: string | undefined | null, fileId: string): FileMetadata | null =>
    (userId ? knownMeta.get(metaKey(userId, fileId)) ?? null : null);

/**
 * Blob URLs created while loading a workspace (inline base64 video turned into an
 * object URL). They are live, unlike a blob URL saved by an earlier session.
 */
const rehydratedBlobs = new Set<string>();

export const markRehydratedBlob = (blobUrl: string): void => {
    rehydratedBlobs.add(blobUrl);
};

export const isRehydratedBlob = (ref: string | undefined | null): boolean =>
    typeof ref === 'string' && rehydratedBlobs.has(ref);

export const uploadWithDeduplication = async (
    userId: string,
    base64Data: string,
    options: UploadWithDeduplicationOptions
): Promise<string> => {
    const pending = inFlightUploads.get(base64Data);
    if (pending && pending.userId === userId) return pending.promise;
    const promise: Promise<string> = performUpload(userId, base64Data, options).finally(() => {
        if (inFlightUploads.get(base64Data)?.promise === promise) inFlightUploads.delete(base64Data);
    });
    inFlightUploads.set(base64Data, { userId, promise });
    return promise;
};

const performUpload = async (
    userId: string,
    base64Data: string,
    options: UploadWithDeduplicationOptions
): Promise<string> => {
    // Skip if already a reference (file://, or a legacy reference from the retired media backend)
    if (isFileReference(base64Data) || base64Data.startsWith('mongo://')) return base64Data;

    // Skip if not base64/blob data
    if (!base64Data.startsWith('data:') && !base64Data.startsWith('blob:')) {
        return base64Data;
    }

    const app = getFirebaseApp();
    if (!app) {
        console.warn('[fileRegistry] Firebase not initialized, keeping base64');
        return base64Data;
    }

    try {
        // Deduplication: content hash → lookup existing
        let contentHash: string | null = null;
        try {
            contentHash = await calculateContentHash(base64Data);
            const existingFile = await findByContentHash(userId, contentHash);
            if (existingFile) {
                console.log(`[fileRegistry] Dedup hit: ${existingFile.id}`);
                rememberStoragePath(userId, existingFile.id, existingFile.storagePath);
                try { options.onDuplicate?.(existingFile); } catch { /* the caller's follow-up must not fail the upload */ }
                return createFileReference(existingFile.id);
            }
        } catch (hashError) {
            console.warn('[fileRegistry] Content hash calc failed:', hashError);
        }

        const storage = getStorage(app);
        const db = getFirestore(app);

        const timestamp = options.timestamp || (Date.now() + Math.floor(Math.random() * 1000));
        const sanitizedWorkspace = sanitizeWorkspaceName(options.workspaceName);
        const storagePath = `users/${userId}/${sanitizedWorkspace}/${options.category}/${mediaFileName(options.category, timestamp, options.extension)}`;

        const storageRef = ref(storage, storagePath);
        await uploadString(storageRef, base64Data, 'data_url');

        const downloadUrl = await getDownloadURL(storageRef);
        const size = getBase64FileSize(base64Data);

        const CONTENT_TYPES: Record<string, string> = {
            mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
            mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4',
            jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', png: 'image/png',
        };
        const contentType = CONTENT_TYPES[options.extension.toLowerCase()] || 'image/png';

        const type: FileMetadata['type'] =
            contentType.startsWith('image/') ? 'image' :
            contentType.startsWith('video/') ? 'video' :
            contentType.startsWith('audio/') ? 'audio' : 'file';

        const fileId = generateFileId();
        const fileMetadata: FileMetadata = {
            id: fileId,
            displayName: `${options.displayNamePrefix}_${timestamp}.${options.extension}`,
            storagePath,
            downloadUrl,
            type,
            contentType,
            size,
            category: options.category,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            nodeId: options.nodeId,
            nodeType: options.nodeType,
            workspaceName: options.workspaceName,
            contentHash: contentHash || undefined,
            prompt: options.prompt,
            iterationCount: options.iterationCount,
            negativePrompt: options.negativePrompt,
            model: options.model,
        };

        // Strip undefined fields (Firestore doesn't accept them)
        const cleanMetadata = Object.fromEntries(
            Object.entries(fileMetadata).filter(([_, v]) => v !== undefined)
        ) as FileMetadata;

        const docRef = doc(db, 'users', userId, 'files', fileId);
        await setDoc(docRef, cleanMetadata);
        // The entry is known from here on: showing the file needs no read of the registry
        noteFileMeta(userId, cleanMetadata, fileId);

        // Notify listeners (e.g. StorageNode) that a new file appeared
        try {
            window.dispatchEvent(new CustomEvent('files-changed', {
                detail: { userId, id: fileId, fileRef: createFileReference(fileId), category: options.category },
            }));
        } catch (_) { /* non-browser */ }

        console.log(`[fileRegistry] Uploaded: ${fileId} → ${storagePath}`);
        return createFileReference(fileId);
    } catch (e: any) {
        console.error('[fileRegistry] Upload failed:', e);
        return base64Data;
    }
};

/**
 * Register a new file in the registry
 */
export const registerFile = async (
    userId: string,
    storagePath: string,
    metadata: Partial<FileMetadata>
): Promise<FileMetadata> => {
    const app = getFirebaseApp();
    if (!app) throw new Error('Firebase not initialized');
    
    const db = getFirestore(app);
    const storage = getStorage(app);
    
    // Generate file ID
    const fileId = generateFileId();
    
    // Get download URL
    const storageRef = ref(storage, storagePath);
    const downloadUrl = await getDownloadURL(storageRef);
    
    // Get file metadata from storage if not provided
    let size = metadata.size || 0;
    let contentType = metadata.contentType || 'application/octet-stream';
    
    if (!metadata.size || !metadata.contentType) {
        try {
            const storageMetadata = await getMetadata(storageRef);
            size = storageMetadata.size;
            contentType = storageMetadata.contentType || contentType;
        } catch (e) {
            console.warn('Could not get storage metadata:', e);
        }
    }
    
    // Determine file type from content type
    const type: FileMetadata['type'] = 
        contentType.startsWith('image/') ? 'image' :
        contentType.startsWith('video/') ? 'video' :
            contentType.startsWith('audio/') ? 'audio' : 'file';
    
    // Create file metadata
    const fileMetadata: FileMetadata = {
        id: fileId,
        displayName: metadata.displayName || storagePath.split('/').pop() || 'Untitled',
        storagePath,
        downloadUrl,
        type,
        contentType,
        size,
        category: metadata.category || 'Other',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        nodeId: metadata.nodeId,
        workspaceName: metadata.workspaceName,
    };
    
    // Save to Firestore
    const docRef = doc(db, 'users', userId, 'files', fileId);
    await setDoc(docRef, fileMetadata);
    noteFileMeta(userId, fileMetadata, fileId);

    return fileMetadata;
};

/**
 * Get file metadata by ID
 */
export const getFileById = async (
    userId: string,
    fileId: string,
    refreshUrl: boolean = false
): Promise<FileMetadata | null> => {
    const app = getFirebaseApp();
    if (!app) {
        console.warn('🔍 getFileById: Firebase app not initialized');
        return null;
    }
    
    const db = getFirestore(app);
    const docRef = doc(db, 'users', userId, 'files', fileId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
        console.warn('🔍 getFileById: File not found:', fileId);
        return null;
    }
    
    const metadata = docSnap.data() as FileMetadata;
    
    // Optionally refresh the download URL (Firebase URLs expire)
    if (refreshUrl) {
        try {
            const storage = getStorage(app);
            const storageRef = ref(storage, metadata.storagePath);
            metadata.downloadUrl = await getDownloadURL(storageRef);
            
            // Update the stored URL
            await updateDoc(docRef, { downloadUrl: metadata.downloadUrl, updatedAt: Date.now() });
        } catch (e) {
            console.warn('Could not refresh download URL:', e);
        }
    }

    noteFileMeta(userId, metadata, fileId);
    return metadata;
};

/**
 * Resolve a file reference or URL to a usable URL
 * - If it's a file:// reference, looks up the current URL
 * - If it's already a URL (http/https/data/blob), returns it as-is
 */
export const resolveFileUrl = async (
    userId: string,
    fileRefOrUrl: string | undefined | null
): Promise<string | null> => {
    if (!fileRefOrUrl) return null;
    
    // If it's a file reference, look it up
    if (isFileReference(fileRefOrUrl)) {
        const fileId = extractFileId(fileRefOrUrl);
        if (!fileId) {
            console.warn('🔍 resolveFileUrl: Could not extract fileId from', fileRefOrUrl);
            return null;
        }
        
        try {
            const metadata = await getFileById(userId, fileId, true);
            if (!metadata?.downloadUrl) {
                console.warn('🔍 resolveFileUrl: No download URL for', fileId);
            }
            return metadata?.downloadUrl || null;
        } catch (e: any) {
            console.error('🔍 resolveFileUrl: Error getting file', fileId, e.message);
            return null;
        }
    }
    
    // If it's already a URL, return as-is
    if (fileRefOrUrl.startsWith('http') || 
        fileRefOrUrl.startsWith('data:') || 
        fileRefOrUrl.startsWith('blob:')) {
        return fileRefOrUrl;
    }
    
    return null;
};

/**
 * Update file metadata (rename, move, etc.)
 */
export const updateFileMetadata = async (
    userId: string,
    fileId: string,
    updates: Partial<Pick<FileMetadata, 'displayName' | 'category' | 'nodeId' | 'workspaceName'>>
): Promise<boolean> => {
    const app = getFirebaseApp();
    if (!app) return false;
    
    const db = getFirestore(app);
    const docRef = doc(db, 'users', userId, 'files', fileId);
    
    try {
        await updateDoc(docRef, {
            ...updates,
            updatedAt: Date.now(),
        });
        return true;
    } catch (e) {
        console.error('Failed to update file metadata:', e);
        return false;
    }
};

/**
 * Delete a file from both Storage and Registry
 */
export const deleteRegisteredFile = async (
    userId: string,
    fileId: string
): Promise<boolean> => {
    const app = getFirebaseApp();
    if (!app) return false;
    
    const db = getFirestore(app);
    const storage = getStorage(app);
    
    try {
        // Get file metadata first
        const metadata = await getFileById(userId, fileId);
        if (!metadata) return false;
        
        // Delete from Storage
        try {
            const storageRef = ref(storage, metadata.storagePath);
            await deleteObject(storageRef);
        } catch (e) {
            console.warn('Storage file may already be deleted:', e);
        }
        
        // Delete from Firestore
        const docRef = doc(db, 'users', userId, 'files', fileId);
        await deleteDoc(docRef);
        forgetFile(userId, fileId);

        return true;
    } catch (e) {
        console.error('Failed to delete file:', e);
        return false;
    }
};

/**
 * List all files for a user
 */
export const listUserFiles = async (
    userId: string,
    filters?: {
        category?: StorageCategory;
        type?: FileMetadata['type'];
        nodeId?: string;
    }
): Promise<FileMetadata[]> => {
    const app = getFirebaseApp();
    if (!app) return [];
    
    const db = getFirestore(app);
    const filesRef = collection(db, 'users', userId, 'files');
    
    let q = query(filesRef);
    
    // Note: Firestore doesn't support multiple inequality filters,
    // so we filter in memory after fetching
    const snapshot = await getDocs(q);
    
    let files = snapshot.docs.map(doc => doc.data() as FileMetadata);
    // The whole list is here anyway: where each file is stored is remembered for this device
    for (const file of files) rememberStoragePath(userId, file.id, file.storagePath);

    // Apply filters
    if (filters?.category) {
        files = files.filter(f => f.category === filters.category);
    }
    if (filters?.type) {
        files = files.filter(f => f.type === filters.type);
    }
    if (filters?.nodeId) {
        files = files.filter(f => f.nodeId === filters.nodeId);
    }
    
    // Sort by most recent first
    files.sort((a, b) => b.updatedAt - a.updatedAt);
    
    return files;
};

/**
 * Subscribe to real-time file list updates
 */
export const subscribeToUserFiles = (
    userId: string,
    callback: (files: FileMetadata[]) => void
): (() => void) => {
    const app = getFirebaseApp();
    if (!app) return () => {};
    
    const db = getFirestore(app);
    const filesRef = collection(db, 'users', userId, 'files');
    
    return onSnapshot(filesRef, (snapshot) => {
        const files = snapshot.docs.map(doc => doc.data() as FileMetadata);
        for (const file of files) rememberStoragePath(userId, file.id, file.storagePath);
        callback(files.sort((a, b) => b.updatedAt - a.updatedAt));
    });
};

/**
 * Scan Firebase Storage and register any unregistered files
 * This is used for migrating existing files to the new system
 */
export const registerExistingFiles = async (
    userId: string,
    onProgress?: (current: number, total: number, status: string) => void
): Promise<{ registered: number; skipped: number; errors: string[] }> => {
    const app = getFirebaseApp();
    if (!app) return { registered: 0, skipped: 0, errors: ['Firebase not initialized'] };
    
    const storage = getStorage(app);
    const db = getFirestore(app);
    
    const errors: string[] = [];
    let registered = 0;
    let skipped = 0;
    
    try {
        // Get all existing registered files
        const existingFiles = await listUserFiles(userId);
        const existingPaths = new Set(existingFiles.map(f => f.storagePath));
        
        // Scan storage
        const userStorageRef = ref(storage, `users/${userId}`);
        onProgress?.(0, 0, 'Scanning storage...');
        
        const allFiles: { ref: any; path: string }[] = [];
        
        const scanFolder = async (folderRef: any): Promise<void> => {
            try {
                const result = await listAll(folderRef);
                for (const item of result.items) {
                    allFiles.push({ ref: item, path: item.fullPath });
                }
                for (const prefix of result.prefixes) {
                    await scanFolder(prefix);
                }
            } catch (e) {
                console.warn('Error scanning folder:', e);
            }
        };
        
        await scanFolder(userStorageRef);
        
        onProgress?.(0, allFiles.length, `Found ${allFiles.length} files`);
        
        // Register unregistered files
        const batch = writeBatch(db);
        let batchCount = 0;
        const MAX_BATCH = 400; // Firestore batch limit is 500
        
        for (let i = 0; i < allFiles.length; i++) {
            const file = allFiles[i];
            
            onProgress?.(i + 1, allFiles.length, `Processing: ${file.path.split('/').pop()}`);
            
            // Skip if already registered
            if (existingPaths.has(file.path)) {
                skipped++;
                continue;
            }
            
            try {
                const [url, metadata] = await Promise.all([
                    getDownloadURL(file.ref),
                    getMetadata(file.ref),
                ]);
                
                const fileId = generateFileId();
                const contentType = metadata.contentType || 'application/octet-stream';
                const type: FileMetadata['type'] = 
                    contentType.startsWith('image/') ? 'image' :
                    contentType.startsWith('video/') ? 'video' :
            contentType.startsWith('audio/') ? 'audio' : 'file';
                
                // Determine category from path
                let category: StorageCategory = 'Other';
                if (file.path.includes('/Generated_Images/')) category = 'Generated_Images';
                else if (file.path.includes('/Uploaded_Images/')) category = 'Uploaded_Images';
                else if (file.path.includes('/Videos/')) category = 'Videos';
                else if (file.path.includes('/Sketches/')) category = 'Sketches';
                else if (file.path.includes('/Characters/')) category = 'Characters';
                else if (file.path.includes('/Sets/')) category = 'Sets';
                else if (file.ref.name.startsWith('gen_')) category = 'Generated_Images';
                else if (file.ref.name.startsWith('ref_')) category = 'Characters';
                else if (file.ref.name.includes('video') || file.ref.name.endsWith('.mp4')) category = 'Videos';
                else if (file.ref.name.includes('sketch')) category = 'Sketches';
                
                const fileMetadata: FileMetadata = {
                    id: fileId,
                    displayName: file.ref.name,
                    storagePath: file.path,
                    downloadUrl: url,
                    type,
                    contentType,
                    size: metadata.size,
                    category,
                    createdAt: metadata.timeCreated ? new Date(metadata.timeCreated).getTime() : Date.now(),
                    updatedAt: Date.now(),
                };
                
                const docRef = doc(db, 'users', userId, 'files', fileId);
                batch.set(docRef, fileMetadata);
                batchCount++;
                registered++;
                
                // Commit batch if it's getting full
                if (batchCount >= MAX_BATCH) {
                    await batch.commit();
                    batchCount = 0;
                }
                
            } catch (e: any) {
                errors.push(`${file.path}: ${e.message}`);
            }
        }
        
        // Commit remaining
        if (batchCount > 0) {
            await batch.commit();
        }
        
        onProgress?.(allFiles.length, allFiles.length, 'Complete!');
        
    } catch (e: any) {
        errors.push(`Scan error: ${e.message}`);
    }
    
    return { registered, skipped, errors };
};

/**
 * Find files that might match a broken node reference
 * Used for manual re-linking
 */
export const findMatchingFiles = async (
    userId: string,
    searchTerm: string,
    type?: FileMetadata['type']
): Promise<FileMetadata[]> => {
    const allFiles = await listUserFiles(userId, { type });
    
    const term = searchTerm.toLowerCase();
    return allFiles.filter(f => 
        f.displayName.toLowerCase().includes(term) ||
        f.storagePath.toLowerCase().includes(term)
    );
};

export default {
    isFileReference,
    extractFileId,
    createFileReference,
    calculateContentHash,
    findByContentHash,
    uploadWithDeduplication,
    registerFile,
    getFileById,
    getFileMeta,
    resolveFileUrl,
    updateFileMetadata,
    deleteRegisteredFile,
    listUserFiles,
    subscribeToUserFiles,
    registerExistingFiles,
    findMatchingFiles,
};

